import type { UriTemplate } from '../utils/request/api-routes';
import type { SearchParams } from '../utils/request/url';
import type { CriteriaChoiceUserToDelete, UpdatingCriteriasState, UpdatingDataCriteria, UpdatingDataValue } from '~/components/Profile/model';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { UriType } from '~/model/GlobalTypes';
import type { UserType } from '~/model/UserType';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { AcceptLanguage } from '~/const/locale';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { isGuestUser } from '~/utils/user/user';
import { filterUserCriteria } from '../utils/user-criteria/filter';

export function useUserCriteria({
  child,
  interaction,
  userCriteriaFilter,
  onSubmit = ({ callback }) => Promise.resolve(callback()),
}: {
  child?: UserType;
  interaction?: UriType;
  userCriteriaFilter?: SearchParams;
  onSubmit?: ({ callback }: { callback: () => Promise<void> }) => Promise<void>;
}) {
  const locale = useLocale();
  const { user } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();

  const {
    status: getStatus,
    startRequest: getStartRequest,
    handleError: getHandleError,
    handleSuccess: getHandleSuccess,
  } = useRequestState();

  const {
    status: submitStatus,
    startRequest: submitStartRequest,
    handleError: submitHandleError,
    handleSuccess: submitHandleSuccess,
  } = useRequestState();

  const [userCriteria, setUserCriteria] = useState<CriteriaChoiceUserType[]>();

  const currentUser = child || user;

  const updateUserCriteria = (
    data: Array<CriteriaChoiceUserType & CriteriaChoiceUserToDelete>,
  ) => {
    setUserCriteria(state => filterUserCriteria(state || [], data));
  };

  const submit = async (updatingData: UpdatingCriteriasState) => {
    const data: UpdatingDataCriteria[] = Object.keys(updatingData).reduce(
      (acc, uri) => {
        acc.push({ ...updatingData[uri] });
        return acc;
      },
      [],
    );

    const items: Array<UpdatingDataCriteria> = data.filter(item => !item.ignore);

    submitStartRequest();

    const promise = await Promise.all(
      items.map(async (item) => {
        const criterion = item.value;
        const criterionChoiceId = item.userCriteriaChoiceId;

        const payloadBase = {
          criterion: item.criteriaUri,
          ...item.value,
          ...(child?.id && {
            created_for: `/api/users/${child.id}`,
          }),
          ...interaction && { interaction },
        };

        function getPathname(
          criterionChoiceId: number | null,
        ): UriTemplate {
          if (criterionChoiceId) {
            return `criterion_choice_users/${criterionChoiceId}`;
          }
          return 'criterion_choice_users';
        }

        function getMethod(
          criterion: UpdatingDataValue,
          criterionChoiceId: number | null,
        ): string {
          if (criterion === null) {
            return 'DELETE';
          }
          if (criterionChoiceId) {
            return 'PUT';
          }
          return 'POST';
        }

        const response = await fetchItem<CriteriaChoiceUserType & CriteriaChoiceUserToDelete>({
          pathname: getPathname(criterionChoiceId),
          headers: {
            acceptLanguage: AcceptLanguage[locale],
          },
          method: getMethod(criterion, criterionChoiceId),
          revalidate: 0,
          body: {
            ...payloadBase,
          },
        });

        if (response['hydra:description']) {
          submitHandleError();
          setError(response);
          return response;
        }

        return response.id ? response : { ...response, '@id': item.criteriaUri };
      }),
    );

    if (promise.some(res => res['hydra:description'])) {
      submitHandleError();
      return;
    }

    await onSubmit({
      callback: async () => updateUserCriteria(promise),
    });

    submitHandleSuccess();
  };

  const getUserCriteria = useCallback(
    async (filter = userCriteriaFilter) => {
      if (!!currentUser?.id && !isGuestUser(currentUser) && !!locale && !!filter) {
        getStartRequest();
        const pathname: UriTemplate = `users/${currentUser.id}/criterion_choice_users`;
        const response = await fetchCollection<CriteriaChoiceUserType & CriteriaChoiceUserToDelete>({
          pathname,
          headers: {
            acceptLanguage: AcceptLanguage[locale],
          },
          revalidate: 0,
          searchParams: {
            'groups[]': 'criterion_choice_translations',
            ...filter,
          },
          unpaginate: true,
        });

        if (response['hydra:description']) {
          getHandleError();
          return setError(response);
        }

        updateUserCriteria(response['hydra:member']);
        getHandleSuccess();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser, locale, userCriteriaFilter?.length],
  );

  const uriList = useMemo(() => {
    return userCriteria?.map(criterion => criterion.criterion?.['@id']);
  }, [userCriteria]);

  useEffect(() => {
    getUserCriteria();
  }, [getUserCriteria]);

  return {
    isFetching: getStatus === RequestStatus.PENDING,
    isUpdating: submitStatus === RequestStatus.PENDING,
    getUserCriteria,
    submit,
    uriList,
    userCriteria,
  };
}
